import type { LC, PropsWithChildren } from '@use-gpu/live';
import type { ComputeToPass, CommandToBuffer, ComputeCounter } from './types';

import { yeet, memo } from '@use-gpu/live';
import { useDeviceContext } from '../providers/device-provider';
import { useInspectable } from '../hooks/useInspectable'
import { QueueReconciler } from '../reconcilers/index';

const {quote} = QueueReconciler;

export type ComputePassProps = PropsWithChildren<{
  calls: {
    pre?: CommandToBuffer[],
    compute?: ComputeToPass[],
  },
  immediate?: boolean,
}>;

const NO_OPS: any[] = [];
const toArray = <T>(x?: T[]): T[] => Array.isArray(x) ? x : NO_OPS;

const label = 'ComputePass';
const LABEL = { label };

/** Compute pass.

Executes all compute calls. Can optionally run immediately instead of per-frame.
*/
export const ComputePass: LC<ComputePassProps> = memo((props: ComputePassProps) => {
  const {
    immediate,
    calls,
  } = props;

  const inspect = useInspectable();

  const device = useDeviceContext();

  const pres = toArray(calls['pre'] as CommandToBuffer[]);
  const computes = toArray(calls['compute'] as ComputeToPass[]);

  const inspected = inspect({
    render: {
      workgroups: 0,
      samples: 0,
    },
  });

  const run = () => {
    let ws = 0;
    let ss = 0;

    const countDispatch = (w: number, s: number) => { ws += w; ss += s; };

    const queue: GPUCommandBuffer[] = []
    for (const f of pres) {
      const q = f();
      if (Array.isArray(q)) queue.push(...q);
      else if (q) queue.push(q);
    }

    if (computes.length) {
      const commandEncoder = device.createCommandEncoder(LABEL);

      const passEncoder = commandEncoder.beginComputePass();
      for (const f of computes) f(passEncoder, countDispatch);
      passEncoder.end();

      const command = commandEncoder.finish();
      queue.push(command);
    }
    device.queue.submit(queue);

    inspected.render.workgroups = ws;
    inspected.render.samples = ss;

    return null;
  };

  return immediate ? run() : quote(yeet(run));

}, 'ComputePass');
